import { z } from 'zod';

import { ARI_ASSISTANT_UI_COMPONENT_TYPES } from './component-catalog';
import type { UIComponent, ValidationIssue } from './types';
import type { ServerUIComponentType } from './component-catalog';

/**
 * Minimal boundary-validation schema for bot-UI payloads received on the
 * WebRTC data channel. This is deliberately shallow — we verify the envelope
 * has a known `type` discriminator, and trust the server for nested field
 * shapes. Item 5 of the dynamic-ui-hardening plan replaces this with a
 * codegen-driven strict schema generated from the ari worker's JSON Schema.
 *
 * The intent is to catch wildly malformed payloads (missing type, wrong type,
 * non-string type) at the boundary and surface them as a visible
 * `__validation_error__` component rather than letting React render `undefined`
 * fields and blow up mid-tree.
 */

const KNOWN_TYPES = ARI_ASSISTANT_UI_COMPONENT_TYPES as unknown as [ServerUIComponentType, ...ServerUIComponentType[]];

// Forces a compile-time error if a `UIComponent` variant is added without
// updating `ARI_ASSISTANT_UI_COMPONENT_CATALOG`, so the boundary validator
// can't silently drift.
// The two halves catch opposite drifts: an unknown literal in `KNOWN_TYPES`,
// and a missing `ServerUIComponentType` from `KNOWN_TYPES`.
const _knownTypeMembersAreValid: readonly ServerUIComponentType[] = KNOWN_TYPES;
type _KnownTypesExhaustive = Exclude<ServerUIComponentType, typeof KNOWN_TYPES[number]> extends never ? true : never;
const _assertKnownTypesExhaustive: _KnownTypesExhaustive = true;
void _knownTypeMembersAreValid;
void _assertKnownTypesExhaustive;

export const KnownUIComponentType = z.enum(KNOWN_TYPES);

const ChartSeriesSchema = z.object({
  name: z.string(),
  data: z.array(z.number()),
});

/**
 * Chart payloads need `series[*].data.length === categories.length`, mirroring
 * the Pydantic `model_validator` on the ari side. Enforced at the boundary so
 * bad shapes surface as `__validation_error__` rather than exploding ApexCharts.
 */
const BarChartSchema = z
  .object({
    type: z.literal('bar_chart'),
    categories: z.array(z.string()),
    series: z.array(ChartSeriesSchema).min(1),
  })
  .passthrough()
  .refine((v) => v.series.every((s) => s.data.length === v.categories.length), {
    message: 'series[*].data.length must equal categories.length',
  });

const LineChartSchema = z
  .object({
    type: z.literal('line_chart'),
    categories: z.array(z.string()),
    series: z.array(ChartSeriesSchema).min(1),
  })
  .passthrough()
  .refine((v) => v.series.every((s) => s.data.length === v.categories.length), {
    message: 'series[*].data.length must equal categories.length',
  });

const PieChartSchema = z
  .object({
    type: z.literal('pie_chart'),
    slices: z.array(z.object({ label: z.string(), value: z.number() })).min(1),
  })
  .passthrough();

const CHART_TYPES = new Set(['bar_chart', 'line_chart', 'pie_chart']);

function parseChartPayload(value: Record<string, unknown>) {
  switch (value.type) {
    case 'bar_chart':
      return BarChartSchema.safeParse(value);
    case 'line_chart':
      return LineChartSchema.safeParse(value);
    case 'pie_chart':
      return PieChartSchema.safeParse(value);
    default:
      return null;
  }
}

function validateNestedCharts(value: unknown, path: (string | number)[], ctx: z.RefinementCtx): void {
  if (!value || typeof value !== 'object') return;

  if (Array.isArray(value)) {
    value.forEach((item, index) => validateNestedCharts(item, [...path, index], ctx));
    return;
  }

  const record = value as Record<string, unknown>;
  const chartResult = parseChartPayload(record);
  if (chartResult) {
    if (!chartResult.success) {
      chartResult.error.issues.forEach((issue) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...path, ...issue.path],
          message: issue.message,
        });
      });
    }
    return;
  }

  Object.entries(record).forEach(([key, nestedValue]) => {
    validateNestedCharts(nestedValue, [...path, key], ctx);
  });
}

const PassthroughByType = z
  .object({ type: KnownUIComponentType })
  .passthrough()
  .refine((v) => !CHART_TYPES.has(v.type), {
    message: 'chart types must use their strict schema',
  })
  .superRefine((v, ctx) => {
    validateNestedCharts(v, [], ctx);
  });

/**
 * Boundary validation is shallow for most components (discriminator + passthrough),
 * but strict for the three chart variants where length parity is load-bearing.
 * Item 5 of the dynamic-ui-hardening plan replaces the rest with codegen.
 * Render-time access to nested fields is still protected by
 * `ComponentErrorBoundary`.
 */
export const UIComponentSchema: z.ZodType<UIComponent> = z.union([
  BarChartSchema,
  LineChartSchema,
  PieChartSchema,
  PassthroughByType,
]) as unknown as z.ZodType<UIComponent>;

/**
 * Validate a raw server payload, returning either the validated UIComponent
 * or a `__validation_error__` sentinel. Callers push the result into their
 * store for rendering.
 */
export function handleUiPayload(payload: unknown): UIComponent {
  const parsed = UIComponentSchema.safeParse(payload);
  if (parsed.success) return parsed.data;

  const issues: ValidationIssue[] = parsed.error.issues.map((issue) => ({
    path: [...issue.path],
    message: issue.message,
  }));
  return {
    type: '__validation_error__',
    errors: issues,
    raw: payload,
  };
}
